import React, { useContext } from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';

import { Container, Body, Content as DefaultContent } from '../DefaultStyles/Details';
import {
    Footer,
    Title,
    DetailsContainer as DefaultDetailsContainer,
    Details
} from '../DefaultStyles/Shared';
import OverlayPartner from '../../containers/Widget/OverlayPartner';

import CssTransition from '../../components/Transition/CssTransition';
import TransitionContainer from '../../components/Transition/TranstionContainer';
import ToasterAlert from '../../components/Donation/ToasterAlert';
import PetitionForm from '../../components/Petition/PetitionForm';
import RoundImage from '../../components/Image/RoundImage';
import ShareDrawer from '../../components/ShareDrawer';
import { isLongText } from '../../components/Shared/commonFunctions';

import WidgetHeader from '../Widget/WidgetHeader';
import Overlay from '../Widget/Overlay';
import OverlayPublisher from '../Widget/OverlayPublisher';
import OverlayPetition from './PetitionOverlay';

import {
    onBackWithinCard,
    onGoToNextCard,
    setOverlayPartnerVisible,
    setExitingCardProps,
    getExitingCardProps
} from '../Widget/actions';

import { onPetitionComplete, setOverlayPetitionVisible, onInitiated } from './actions';

import { handleSkippingEndCard } from '../../utils/handleSkippingEndCard';
import { cacheFormData } from '../../utils/formCaching/actions';
import { isPortrait } from '../../utils/size';
import { formatThousands, Placeholders } from '../../utils/fillDynamicText';
import { getActionCompletedTime } from '../../utils/actionTiming';
import { CardTypes } from '../../utils/enums';
import { pictureUrl } from '../../utils/imageLoading';
import ThemeContext from '../../utils/theming';
import {
    getNextAction,
    buildConditionalActionsAnswerHistory
} from '../../utils/nextActionUtil';

import { copyFor } from '../../copy/copy';

const Content = styled(DefaultContent)`
    margin-top: 0.5em;
    display: ${() => (isPortrait() ? 'block' : 'flex')};
`;

const DetailsContainer = styled(DefaultDetailsContainer)`
    flex-direction: row;
    align-items: flex-start;
`;

const Description = styled.p`
    object-fit: contain;
    font-family: Lato;
    line-height: 1.5;
    color: #333;
    margin-bottom: 0;
    word-break: break-word;
    font-size: 0.85em;
    font-weight: 700;

    #learnMore {
        font-weight: 700;
        color: ${({ theme }) => theme.colors.primary};
        cursor: pointer;
    }

    b {
        font-weight: 900;
    }
`;

const horizontalBlocks = `
    width: 50%;
    height: 100%;
    margin: 0 0 0 15px;
    &:first-child {
        margin: 0 15px 0 0;
    }
`;
const verticalBlocks = `
    width: 100%;
    &:last-child {
        height: calc(50% + 20px);
    }
`;

const Block = styled.div`
    ${() => (isPortrait() ? verticalBlocks : horizontalBlocks)}
`;

const LearnMore = styled.span`
    font-weight: 800;
    font-size: 0.85em;
    line-height: 2;
    color: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
`;

const Care2Tag = styled.span`
    font-weight: 800;
    font-size: 0.5em;
    color: ${({ theme }) => theme.colors.primary};
    letter-spacing: 1px;
`;

const PetitionDetails = props => {
    const theme = useContext(ThemeContext);
    return (
        <TransitionContainer zIndex={2} isSubmitting={props.isSubmitting}>
            <Container>
                <Overlay />
                <OverlayPublisher />
                <OverlayPartner transitionType="right-to-left" />
                {props.actionType === 'petition' &&
                    <OverlayPetition transitionType="right-to-left" />
                }
                <Body>
                    <WidgetHeader />
                    <Content>
                        <Block>
                            <Title
                                scaleFactor={0.9}
                                isShortText={!isLongText(props.cardTitle, 28)}
                                isLongText={isLongText(props.cardTitle, 60)}
                                isExtraLongText={isLongText(props.cardTitle, 95)}
                                onClick={e =>
                                    props.setOverlayPartnerVisible(true, e, false)
                                }
                                dangerouslySetInnerHTML={{
                                    __html: props.cardTitle
                                }}
                            />
                            {props.isCare2 && (
                                <Care2Tag theme={theme}>
                                    {copyFor(
                                        'miscellaneous',
                                        'poweredByCare2',
                                        'petition'
                                    )}
                                </Care2Tag>
                            )}
                            <DetailsContainer>
                                {!!props.pictureUrl && (
                                    <RoundImage src={props.pictureUrl} size={60} />
                                )}
                                <Details
                                    style={{
                                        marginLeft: !!props.pictureUrl ? '1em' : null
                                    }}
                                    hasPhoto={!!props.pictureUrl}
                                >
                                    <Description
                                        theme={theme}
                                        className="ie-object-fit-contain-polyfill"
                                        dangerouslySetInnerHTML={{
                                            __html: props.cardDescription
                                        }}
                                        onClick={e => {
                                            props.onInitiated(
                                                props.actionInitiated,
                                                props.actionIdentifier
                                            );
                                            if (e.target.id === 'learnMore') {
                                                props.setOverlayPetitionVisible(true);
                                            }
                                        }}
                                        isSingleAction={true}
                                        isLongText={isLongText(props.cardDescription, 90)}
                                        isExtraLongText={isLongText(
                                            props.cardDescription,
                                            160
                                        )}
                                    />
                                    {!props.isDisplayMoreInline &&
                                        !(props.actionType === 'opinion') && (
                                        <LearnMore
                                            theme={theme}
                                            onClick={() => {
                                                props.onInitiated(
                                                    props.actionInitiated,
                                                    props.actionIdentifier
                                                );
                                                props.setOverlayPetitionVisible(true);
                                            }}
                                        >
                                            {copyFor(
                                                'miscellaneous',
                                                'learnMore',
                                                'petition'
                                            )}
                                        </LearnMore>
                                    )}
                                </Details>
                            </DetailsContainer>
                        </Block>
                        <Block>
                            <PetitionForm
                                onSubmit={() => {
                                    props.cacheFormData(props.petitionFormValues);
                                    props.setExitingCardProps(
                                        'petition',
                                        props,
                                        props.activeAction
                                    );
                                    if (props.skipEndCard) {
                                        props.handleSkippingEndCard(
                                            props.nextAction,
                                            props.cards,
                                            props.onGoToNextCard
                                        );
                                    }
                                    props.onPetitionComplete(
                                        props.apiRequestData,
                                        props.actionCompletedTime(),
                                        props.petitionFormValues,
                                        props.isMultiActionsCard,
                                        props.activeAction,
                                        props.skipEndCard
                                    );
                                }}
                                response={props.response}
                                isSubmitting={props.isSubmitting}
                                onInitiated={() =>
                                    props.onInitiated(
                                        props.actionInitiated,
                                        props.actionIdentifier
                                    )
                                }
                            />
                        </Block>
                    </Content>
                    <Footer>
                        <ShareDrawer
                            newsletterSignUpVisible={true}
                            beforeBack={() => {
                                props.setExitingCardProps(
                                    'petition',
                                    props,
                                    props.activeAction
                                );
                            }}
                            goBack={() => {
                                return props.card.type === CardTypes.actionChoice
                                    ? props.onBackWithinCard(
                                          props.actionIdentifier,
                                          props.card,
                                          true
                                      )
                                    : null;
                            }}
                            fullwidth={true}
                            hasBackButton={props.card.type === CardTypes.actionChoice}
                            backgroundColor={'#fff'}
                        />
                    </Footer>
                </Body>
                <TransitionGroup>
                    {props.petitionError && (
                        <CssTransition key="alert" in={props.petitionError}>
                            <ToasterAlert content={props.petitionError} />
                        </CssTransition>
                    )}
                </TransitionGroup>
            </Container>
        </TransitionContainer>
    );
};

PetitionDetails.propTypes = {
    actionCompletedTime: PropTypes.func.isRequired,
    actionIdentifier: PropTypes.string.isRequired,
    actionInitiated: PropTypes.bool,
    activeAction: PropTypes.object.isRequired,
    apiRequestData: PropTypes.object.isRequired,
    cacheFormData: PropTypes.func.isRequired,
    card: PropTypes.object.isRequired,
    cardDescription: PropTypes.string.isRequired,
    cards: PropTypes.array.isRequired,
    cardTitle: PropTypes.string.isRequired,
    handleSkippingEndCard: PropTypes.func.isRequired,
    isCare2: PropTypes.bool.isRequired,
    isDisplayMoreInline: PropTypes.bool.isRequired,
    isMultiActionsCard: PropTypes.bool.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    nextAction: PropTypes.object,
    onBackWithinCard: PropTypes.func.isRequired,
    onGoToNextCard: PropTypes.func.isRequired,
    onInitiated: PropTypes.func.isRequired,
    onPetitionComplete: PropTypes.func.isRequired,
    petitionError: PropTypes.string,
    petitionFormValues: PropTypes.object,
    pictureUrl: PropTypes.string,
    response: PropTypes.string,
    setExitingCardProps: PropTypes.func.isRequired,
    setOverlayPartnerVisible: PropTypes.func.isRequired,
    setOverlayPetitionVisible: PropTypes.func.isRequired,
    skipEndCard: PropTypes.bool
};

export default connect(
    (state, props) => {
        const exitingProps = getExitingCardProps(
            'petition',
            ['/petition', '/petition/start'],
            props.location.pathname,
            state
        );
        if (exitingProps) return exitingProps;

        const actionType = state.widget.activeAction.type;

        const nextAction = getNextAction(
            state.widget.actions,
            state.widget.activeAction.identifier,
            buildConditionalActionsAnswerHistory(state),
            state.widget.cards
        );
        const currCard = state.widget.cards[state.widget.activeAction.order - 1];
        const unformattedCardDescription =
            currCard.subHeading || state.widget.activeAction.description || '';
        const learnMoreString = ` <span id="learnMore">&nbsp;${copyFor(
            'miscellaneous',
            'learnMore',
            'petition'
        )}</span>`;

        const isDisplayMoreInline =
            actionType === 'petition' &&
            ((unformattedCardDescription.length > 50 && isPortrait()) ||
            unformattedCardDescription.length > 110);

        return {
            nextAction,
            actionType,
            actionCompletedTime: getActionCompletedTime(state, 'petition'),
            actionIdentifier: state.widget.activeAction.identifier,
            actionInitiated: state.petition.actionInitiated,
            activeAction: state.widget.activeAction,
            apiRequestData: state.widget.apiRequestData,
            card: currCard,
            cardDescription: unformattedCardDescription
                .concat(isDisplayMoreInline ? learnMoreString : '')
                .replace(
                    new RegExp(Placeholders.ArticleUrl, 'g'),
                    state.widget.articleUrl
                )
                .replace(
                    new RegExp(Placeholders.NumberOfAnswers, 'g'),
                    formatThousands(state.widget.activeAction.noOfAnswers) || '121'
                ),
            cardTitle: (currCard.heading || state.widget.activeAction.name || '').replace(
                /@articleUrl/g,
                state.widget.articleUrl
            ),
            currentFormValues: (state.form['petitionDetailsForm'] || {}).values,
            isCare2: !!state.widget.activeAction.care2Id,
            isDisplayMoreInline,
            isSubmitting: state.petition.isSubmitting,
            response: state.widget.activeAction.text,
            cards: state.widget.cards,
            isMultiActionsCard:
                state.widget.cards[state.widget.activeAction.order - 1].type ===
                CardTypes.multipleActions,
            petitionError: state.petition.petitionError,
            petitionFormValues: (state.form.petitionDetailsForm || {}).values,
            pictureUrl: pictureUrl(state) && pictureUrl(state)[0],
            skipEndCard: state.widget.activeAction.skipEndCard && !!nextAction
        };
    },
    {
        onBackWithinCard,
        onPetitionComplete,
        onGoToNextCard,
        onInitiated,
        handleSkippingEndCard,
        cacheFormData,
        setExitingCardProps,
        setOverlayPartnerVisible,
        setOverlayPetitionVisible
    }
)(PetitionDetails);
